"use client";

import React, { useState, useEffect, useMemo } from "react";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Loader2, CalendarIcon, Clock, ArrowRightIcon } from "lucide-react";

import { pushGtmEvent, GTM_EVENT_BOOK_DEMO_SUCCESS } from "@/lib/gtm";
import { cn, tinaFieldSafe } from "@/utils";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import animationData from "@/assets/success.json";

import dynamic from "next/dynamic";

import {
  fetchAvailableTimes,
  bookMeeting,
  parseAvailabilityData,
  HUBSPOT_TIMEZONES,
  findMatchingHubSpotTimezone,
} from "@/lib/hubspot";

const Lottie = dynamic(() => import("react-lottie-player"), { ssr: false });

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const formSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  companyName: z.string().min(1, "Required"),
  email: z.string().email({
    message: "Invalid email",
  }),
  timezone: z.string().min(1, "Timezone is required"),
  date: z.date({
    required_error: "Select a date",
    message: "Select a date",
  }),
  time: z.date({
    required_error: "Select a time slot",
    message: "Select a time slot",
  }),
  message: z.string().optional(),
  // Add the challenges array
  challenges: z
    .array(z.string())
    .min(1, "Please select at least one challenge"),
});

const CHALLENGES = [
  {
    id: "vulnerability-detection",
    label: "Vulnerability Detection & Prioritization",
  },
  {
    id: "sbom-cbom-management",
    label: "SBOM & CBOM Management",
  },
  {
    id: "post-build-and-product-level-risk-intelligence",
    label: "Post-Build and Product Level Risk Intelligence",
  },
  {
    id: "secure-by-design-assessment-and-compliance",
    label: "Secure-by-Design Assessment & Compliance",
  },
  {
    id: "other",
    label: "Other",
  },
];

export default function BookDemoForm({ formSection }: { formSection: any }) {
  const [isLoading, setIsLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<
    Record<
      string,
      Array<{
        start: Date;
        end: Date;
      }>
    >
  >({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthOffset, setMonthOffset] = useState(0);

  const meetingLinkSlug = formSection?.meetingLinkSlug;
  const fallbackFormUrl = formSection?.fallbackFormUrl;

  // Detect timezone before form initialization
  const detectedTimezone = useMemo(() => {
    const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matchedTz = findMatchingHubSpotTimezone(detectedTz);
    return matchedTz;
  }, []);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      timezone: detectedTimezone,
      date: undefined,
      time: undefined,
      message: "",
      challenges: [], // Add default value for challenges
    },
  });

  // Watch timezone value from form
  const userTimezone = form.watch("timezone");

  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const selectedDate = form.watch("date");

  // Reset selected date and time when timezone changes
  useEffect(() => {
    if (userTimezone) {
      form.resetField("date");
      form.resetField("time");
    }
  }, [userTimezone, form]);

  // Fetch available times when timezone or month offset changes
  useEffect(() => {
    if (!userTimezone) return;

    const loadAvailableTimes = async () => {
      setIsLoading(true);
      try {
        const response = await fetchAvailableTimes(
          userTimezone,
          monthOffset,
          meetingLinkSlug
        );

        if (response.success && response.data) {
          const { dates, timeSlots } = parseAvailabilityData(
            response.data,
            userTimezone
          );
          setAvailableDates(dates);
          setAvailableTimeSlots(timeSlots);
        }
      } catch (error) {
        console.error("Error loading available times:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAvailableTimes();
  }, [userTimezone, monthOffset]);

  // Get time slots for selected date (in user's timezone)
  const timeSlotsForSelectedDate =
    selectedDate &&
    availableTimeSlots[
      dayjs(selectedDate).tz(userTimezone).format("YYYY-MM-DD")
    ]
      ? availableTimeSlots[
          dayjs(selectedDate).tz(userTimezone).format("YYYY-MM-DD")
        ]
      : [];

  async function onSubmit(values) {
    if (!userTimezone) return;

    try {
      setIsLoading(true);

      const selectedTimeSlot = timeSlotsForSelectedDate.find(
        (slot) => slot.start.getTime() === values.time.getTime()
      );

      if (!selectedTimeSlot) {
        throw new Error("Invalid time slot selected");
      }

      const result = await bookMeeting({
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.companyName,
        email: values.email,
        startTime: selectedTimeSlot.start.toISOString(),
        endTime: selectedTimeSlot.end.toISOString(),
        timezone: userTimezone,
        challenges: values.challenges,
        message: values.message,
        meetingLinkSlug: meetingLinkSlug,
      });

      if (result.success) {
        form.reset();
        setIsSuccess(true);
        pushGtmEvent({
          event: GTM_EVENT_BOOK_DEMO_SUCCESS,
          form_id: "book_demo",
          booking_method: "scheduler",
          user_data: {
            email: values.email,
          },
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      form.setError("root", {
        type: "manual",
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="relative">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4 text-foreground-100 [&_label]:mb-1"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">First name (required)</FormLabel>
                <FormControl>
                  <Input placeholder="First name*" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Last name (required)</FormLabel>
                <FormControl>
                  <Input placeholder="Last name*" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="sr-only">
                  Company name (required)
                </FormLabel>
                <FormControl>
                  <Input placeholder="Company name*" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="sr-only">
                  Company email (required)
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Company email*"
                    type="email"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="sr-only">Time zone</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full truncate [&_span]:truncate">
                      <SelectValue placeholder="Detecting timezone..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="!max-w-[350px] md:max-h-[300px] overflow-y-auto">
                    {HUBSPOT_TIMEZONES.map((group, index) => (
                      <SelectGroup key={group.group}>
                        <SelectLabel className="uppercase font-medium pl-3">
                          {group.group}
                        </SelectLabel>
                        {group.zones.map((tz) => (
                          <SelectItem
                            key={tz.value}
                            value={tz.value}
                            className="pl-4"
                          >
                            {tz.label}
                          </SelectItem>
                        ))}
                        {/* Not last group */}
                        {index !== HUBSPOT_TIMEZONES.length - 1 && (
                          <SelectSeparator />
                        )}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="sr-only">Pick a date (required)</FormLabel>
                <Popover
                  open={datePopoverOpen}
                  onOpenChange={setDatePopoverOpen}
                >
                  <FormControl>
                    <PopoverTrigger
                      type="button"
                      // variant="outline"
                      className={cn("field flex items-center gap-2")}
                      // disabled={isLoading}
                    >
                      <CalendarIcon className="h-4 w-4 opacity-50" />
                      {field.value ? (
                        dayjs(field.value)
                          .tz(userTimezone)
                          .format("dddd, Do MMMM")
                      ) : isLoading ? (
                        <span className="text-gray-400/70">
                          Fetching availability...
                        </span>
                      ) : (
                        <span className="text-gray-400/70">Pick a date*</span>
                      )}
                    </PopoverTrigger>
                  </FormControl>
                  <PopoverContent
                    className="w-auto p-0 bg-[#0d0d0d]"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={field.value}
                      month={currentMonth}
                      onMonthChange={(newMonth) => {
                        setCurrentMonth(newMonth);
                        const today = new Date();
                        const monthsDiff =
                          (newMonth.getFullYear() - today.getFullYear()) * 12 +
                          (newMonth.getMonth() - today.getMonth());

                        // Only allow current month (0) and next month (1)
                        if (monthsDiff >= 0 && monthsDiff <= 1) {
                          setMonthOffset(monthsDiff);
                        }
                      }}
                      onSelect={(date) => {
                        field.onChange(date);
                        form.resetField("time");
                        setDatePopoverOpen(false);
                      }}
                      disabled={(date) => {
                        // Disable dates without available slots
                        const dateString = dayjs(date).format("YYYY-MM-DD");
                        return !availableDates.some(
                          (availableDate) =>
                            dayjs(availableDate).format("YYYY-MM-DD") ===
                            dateString
                        );
                      }}
                      startMonth={new Date()}
                      endMonth={
                        new Date(new Date().setMonth(new Date().getMonth() + 1))
                      }
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="sr-only">Pick a time (required)</FormLabel>
                <Popover
                  open={timePopoverOpen}
                  onOpenChange={setTimePopoverOpen}
                  // className="w-full"
                >
                  <FormControl>
                    <PopoverTrigger
                      type="button"
                      className={cn(
                        "field flex items-center gap-2",
                        !selectedDate && "cursor-not-allowed opacity-70"
                      )}
                      disabled={!selectedDate}
                    >
                      <Clock className="h-4 w-4 opacity-50" />
                      {field.value ? (
                        dayjs(field?.value).tz(userTimezone).format("h:mm A")
                      ) : (
                        <span className="text-gray-400/70">Pick a time*</span>
                      )}
                    </PopoverTrigger>
                  </FormControl>
                  <PopoverContent
                    className="w-full p-0 bg-[#0d0d0d]"
                    align="start"
                  >
                    <div className="p-3">
                      <div className="text-sm font-medium mb-3">
                        Available Times:
                      </div>
                      {timeSlotsForSelectedDate.length > 0 ? (
                        <div className="grid grid-cols-3 gap-2.5">
                          {timeSlotsForSelectedDate.map((slot, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              className={cn(
                                "justify-center font-normal text-[13px] px-2 !py-1"
                              )}
                              onClick={() => {
                                field.onChange(slot.start);
                                setTimePopoverOpen(false);
                              }}
                            >
                              {dayjs(slot.start)
                                .tz(userTimezone)
                                .format("h:mma")}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground py-2">
                          No time slots available for this date
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="challenges"
            render={() => (
              <FormItem className="md:col-span-2 mt-4">
                <div className="mb-2">
                  <FormLabel className="h6">
                    Select the challenges you're urgently looking to solve for:
                  </FormLabel>
                  {/* <FormMessage /> */}
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {CHALLENGES.map((challenge, index) => (
                    <FormField
                      key={index}
                      control={form.control}
                      name="challenges"
                      render={({ field }) => {
                        return (
                          <FormItem className="flex flex-row items-start space-x-3">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(challenge.id)}
                                className="size-5 data-[state=checked]:bg-primary-400 data-[state=checked]:text-primary-foreground bg-white/20 border-gray-500"
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([
                                        ...field.value,
                                        challenge.id,
                                      ])
                                    : field.onChange(
                                        field.value?.filter(
                                          (value) => value !== challenge.id
                                        )
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal h6 !cursor-pointer text-muted-foreground">
                              {challenge.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
              </FormItem>
            )}
          />
          <div className="md:col-span-2 w-full">
            <Button
              type="submit"
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="animate-spin size-5" />
              ) : (
                "Schedule Demo"
              )}
            </Button>

            <div className="mt-4">
              {form.formState.errors.root && (
                <div className="flex flex-wrap text-center items-center justify-center gap-2">
                  <div className="text-red-500 text-sm font-medium text-center capitalize">
                    {form.formState.errors.root.message}
                  </div>

                  {fallbackFormUrl && (
                    <div className="text-sm text-muted-foreground">
                      {" "}
                      -{" "}
                      <Button variant="link" asChild className="!px-0">
                        <a
                          href={fallbackFormUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          // className="text underline-offset-4 hover:underline"
                        >
                          Book Demo Directly
                          <ArrowRightIcon className="size-4" />
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isSuccess && (
            <SuccessMessage
              title={formSection?.successMessage}
              subtitle={formSection?.successMessageSubtitle}
              tinaData={formSection}
            />
          )}
        </form>
      </Form>
    </div>
  );
}

export const SuccessMessage = ({ title, subtitle, tinaData }) => {
  return (
    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-background rounded-lg text-center px-3">
      <div className="w-44 min-h-44">
        <Lottie
          loop={false}
          play
          animationData={animationData}
          className="success-animation w-48 h-48 [&_svg_path]:fill-secondary-700"
        />
      </div>
      <div className="flex flex-col gap-1">
        {/* <div>[WIP]</div> */}
        <div
          className="h4 mb-1 text-balance"
          data-tina-field={tinaFieldSafe(tinaData, "successMessage")}
        >
          {title}
        </div>

        <div
          className="p-lg text-foreground-500 text-center"
          data-tina-field={tinaFieldSafe(tinaData, "successMessageSubtitle")}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
};
